import {
  getReceiptSystemAccountIdSafe,
  native,
  type Products,
} from '@forest/native-services'
import {
  classifyPurchaseError,
  failureOf,
  restoreSubscription,
  subscribe as subscribeCore,
  tagFailure,
  withFailure,
  type Failure,
  type SubscriptionServiceLevelStatus,
  readOwnerUid,
} from '@forest/paywall-core'
import {
  createMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/solid-query'
import { isHTTPError } from 'ky'
import { grantPermissionDialog } from '~/components/grant-permission-dialog'
import {
  AbortError,
  getAuthenticatedUserInfo,
} from '~/lib/get-authenticated-user-info'
import { openIntercom } from '~/lib/intercom'
import { isConnectivityError } from '~/lib/is-connectivity-error'
import { appVersion, platform } from '~/lib/params'
import { m } from '~/paraglide/messages'
import { api } from '~/services/api'
import { closeDialog, showDialog } from '~/store/dialog'
import { closePlusPaywall } from '~/store/plus-paywall'
import { $serviceLevelStatus } from '~/store/service-level'
import { closeTrialTimelineView } from '~/store/trial-timeline-view'
import { progressesQueryKey } from './progresses'
import {
  classifyRestoreError,
  type RestoreErrorBranch,
} from './restore-helpers'
import { classifySubscribeIntercomNode } from './subscribe-helpers'

export interface SubscribeMutationVariables {
  products: Products
  receiptSystemAccountId: string | null
  isTrialEligible: boolean
}

export { classifyRestoreError, classifySubscribeIntercomNode }

const showGrantPermissionDialogIfNeeded = async (trialEndDate: string) => {
  const granted = await native.permission
    .getHasNotificationPermission()
    .then((r) => r.success && r.data)
    .catch(() => false)
  if (granted) return
  await grantPermissionDialog.open(trialEndDate)
}

export const useSubscribeMutation = () => {
  const queryClient = useQueryClient()
  return createMutation(() => ({
    mutationFn: async (
      vars: SubscribeMutationVariables,
    ): Promise<{
      status: SubscriptionServiceLevelStatus | null
      isTrial: boolean
    }> => {
      const product = vars.products.annualEarlyBird
      // The bootstrap account id was fetched while the user could still be a
      // guest; a guest upgrade switches accounts, so refetch it post-upgrade.
      let receiptSystemAccountId = vars.receiptSystemAccountId
      const userInfo = await getAuthenticatedUserInfo({
        onAfterGuestUpgrade: async () => {
          receiptSystemAccountId = await getReceiptSystemAccountIdSafe(
            appVersion || '0.0.0',
          )
        },
      }).catch((error: unknown) => {
        throw tagFailure(error, 'auth')
      })
      const status = await subscribeCore({
        api,
        platform,
        userId: userInfo.user_id,
        product,
        nativePurchase: native.iap.purchase,
        closeTransaction: native.iap.closeTransaction,
        getAllUnclosedTransactions: native.iap.getAllUnclosedTransactions,
        receiptSystemAccountId,
        logEvent: native.general.logEvent,
      })
      return { status, isTrial: vars.isTrialEligible }
    },
    onSuccess: async ({ status }, variables) => {
      const product = variables.products.annualEarlyBird
      const isTrialSuccess =
        status?.service_level === 'subscription' &&
        status.subscription.is_in_trial
      native.general.logEvent({
        event_name: isTrialSuccess
          ? 'plus_trial_success'
          : 'plus_subscribe_success',
        event_params: { sku_id: product.sku },
      })

      if (status) $serviceLevelStatus.set(status)
      native.user.refreshServiceLevel?.()
      queryClient.invalidateQueries({ queryKey: ['user'] })
      queryClient.invalidateQueries({ queryKey: progressesQueryKey })

      closeTrialTimelineView()

      if (
        platform !== 'china_android' &&
        status?.service_level === 'subscription' &&
        variables.isTrialEligible &&
        status.subscription?.expires_at
      ) {
        await showGrantPermissionDialogIfNeeded(status.subscription.expires_at)
      }
    },
    onError: (error) => {
      if (error instanceof AbortError) return

      const intent = classifyPurchaseError(error)
      switch (intent.kind) {
        case 'payment_timeout':
          if (intent.detail === 'interrupted') return
          showDialog({
            title: m.dialog_payment_timeout_title(),
            description: withFailure(
              m.dialog_payment_timeout_content({ error: intent.detail }),
              intent.failure,
            ),
            primaryButtonText: m.ok(),
            primaryAction: () => closeDialog(),
          })
          return
        case 'illegal_transaction_restore':
          showDialog({
            title: m.dialog_illegal_transaction_title(),
            description: withFailure(
              platform === 'china_android'
                ? m.dialog_illegal_transaction_content_cna()
                : m.dialog_illegal_transaction_content(),
              intent.failure,
            ),
            primaryButtonText: m.restore_purchase(),
            asyncPrimaryAction: async () => {
              try {
                await runRestore(queryClient)
                showDialog({
                  title: m.dialog_restore_success_title(),
                  description: m.dialog_restore_success_content(),
                  primaryButtonText: m.awesome(),
                  primaryAction: () => closePlusPaywall(),
                })
              } catch (restoreError) {
                await handleRestoreErrorBranch(
                  restoreError,
                  classifyRestoreError(restoreError, platform),
                )
              }
            },
          })
          return
        case 'no_internet':
          showConnectionIssueDialog(intent.failure)
          return
        case 'subscription_conflict':
          void showSubscriptionConflictDialog(intent.ownerUid, intent.failure)
          return
        case 'http_error':
        case 'unknown': {
          // isConnectivityError also catches WKWebView's stacked `Load failed`,
          // which the failure code's cause may only see as unexpected.
          if (isConnectivityError(error)) {
            showConnectionIssueDialog(intent.failure)
            return
          }
          const node = classifySubscribeIntercomNode(error)
          showDialog({
            title: m.dialog_unknown_error_title(),
            description: withFailure(
              m.dialog_unknown_error_content({
                error_code: intent.failure.code,
              }),
              intent.failure,
              { codeInDescription: true },
            ),
            primaryButtonText: m.contact_cs(),
            primaryAction: () => {
              closeDialog()
              openIntercom({
                node,
                orderId: intent.failure.purchaseId?.toString(),
                additional: { error_code: intent.failure.code },
              })
            },
          })
          return
        }
        default:
          intent satisfies never
      }
    },
  }))
}

function showConnectionIssueDialog(failure: Failure) {
  showDialog({
    title: m.dialog_connection_issue_title(),
    description: withFailure(m.dialog_connection_issue_description(), failure),
    primaryButtonText: m.ok(),
    primaryAction: () => closeDialog(),
  })
}

async function runRestore(queryClient: QueryClient): Promise<void> {
  const userInfo = await getAuthenticatedUserInfo().catch((error: unknown) => {
    throw tagFailure(error, 'restore_auth')
  })
  const current = $serviceLevelStatus.get()
  const currentSubscriptionExpiresAt =
    current?.service_level === 'subscription'
      ? current.subscription.expires_at
      : null
  const status = await restoreSubscription({
    api,
    userId: userInfo.user_id,
    platform,
    getAllUnclosedTransactions: native.iap.getAllUnclosedTransactions,
    getAllEntitlements: native.iap.getAllEntitlements,
    closeTransaction: native.iap.closeTransaction,
    currentSubscriptionExpiresAt,
  })
  if (status) $serviceLevelStatus.set(status)
  native.user.refreshServiceLevel?.()
  queryClient.invalidateQueries({ queryKey: ['user'] })
  queryClient.invalidateQueries({ queryKey: progressesQueryKey })
}

const appStoreAccountLabel = () =>
  platform === 'ios' ? m.apple_id() : m.google_play_store_id()

async function handleRestoreErrorBranch(
  error: unknown,
  branch: RestoreErrorBranch,
) {
  const failure = failureOf(error, 'restore')
  switch (branch.kind) {
    case 'no_transaction':
      showDialog({
        title: m.dialog_restore_no_transaction_title(),
        description: withFailure(
          platform === 'china_android'
            ? m.dialog_restore_no_transaction_content_cna()
            : m.dialog_restore_no_transaction_content({
                app_store_account: appStoreAccountLabel(),
              }),
          failure,
        ),
        primaryButtonText: m.contact_cs(),
        primaryAction: () => {
          closeDialog()
          openIntercom({
            node: branch.intercomNode,
            orderId: failure.purchaseId?.toString(),
            additional: { error_code: failure.code },
          })
        },
      })
      return
    case 'owner_uid_conflict':
      await showSubscriptionConflictDialog(await readOwnerUid(error), failure)
      return
    case 'forest_account_mismatch':
      showDialog({
        title: m.dialog_restore_error_title(),
        description: withFailure(
          m.dialog_restore_error_forest_account_content({
            app_store_account: appStoreAccountLabel(),
          }),
          failure,
        ),
        primaryButtonText: m.ok(),
        primaryAction: () => closeDialog(),
      })
      return
    case 'transaction_expired':
      showDialog({
        title: m.dialog_transaction_expired_title(),
        description: withFailure(
          m.dialog_transaction_expired_content(),
          failure,
        ),
        primaryButtonText: m.ok(),
        primaryAction: () => closeDialog(),
      })
      return
    case 'unexpected_http':
      showUnexpectedRestoreErrorDialog(branch.intercomNode, failure)
      return
    case 'connection':
      showDialog({
        title: m.dialog_connection_issue_title(),
        description: withFailure(
          m.dialog_connection_issue_description(),
          failure,
        ),
        primaryButtonText: m.ok(),
        primaryAction: () => closeDialog(),
      })
      return
    case 'unexpected':
      showUnexpectedRestoreErrorDialog(branch.intercomNode, failure)
  }
}

function showUnexpectedRestoreErrorDialog(
  intercomNode: string,
  failure: Failure,
) {
  showDialog({
    title: m.dialog_restore_error_title(),
    description: withFailure(
      m.dialog_unknown_error_content({ error_code: failure.code }),
      failure,
      { codeInDescription: true },
    ),
    primaryButtonText: m.contact_cs(),
    primaryAction: () => {
      closeDialog()
      openIntercom({
        node: intercomNode,
        orderId: failure.purchaseId?.toString(),
        additional: { error_code: failure.code },
      })
    },
  })
}

/**
 * A subscription conflict: another Forest account owns the subscription. Names
 * that account, or — when it is deleted or unknown — sends the user to manage
 * the store subscription.
 */
async function showSubscriptionConflictDialog(
  ownerUid: number | null,
  failure: Failure,
) {
  const showDeletedAccountDialog = () =>
    showDialog({
      title: m.dialog_subscription_link_delete_account_title(),
      description: withFailure(
        m.dialog_subscription_link_delete_account_content(),
        failure,
      ),
      primaryButtonText: m.btn_subscription_management(),
      primaryAction: () => {
        closeDialog()
        native.general.openURL(
          'forestapp://instant/system_subscription_management',
        )
      },
    })
  if (ownerUid === null) {
    showDeletedAccountDialog()
    return
  }
  let userName = ''
  let userEmail = ''
  try {
    const info = await api.service
      .get(`user/users/${ownerUid}/info`)
      .json<{ name: string; email: string }>()
    userName = info.name
    userEmail = info.email
  } catch (lookupError) {
    if (isHTTPError(lookupError) && lookupError.response.status === 404) {
      showDeletedAccountDialog()
      return
    }
  }
  showDialog({
    title: m.dialog_subscription_conflict_title(),
    description: withFailure(
      m.dialog_subscription_conflict_content({
        user_name: userName,
        user_account: userEmail,
      }),
      failure,
    ),
    primaryButtonText: m.ok(),
    primaryAction: () => closeDialog(),
  })
}
